// Single source of truth for the admin-dashboard pages a superadmin can grant
// to a sub-admin. Drives (1) the permission matrix on the Roles page, (2) the
// sub-admin sidebar menu, and (3) the per-page access guard.
//
// `resource`    - legacy key (the frontend route). Kept as the fallback permission
//                 key for pages whose backend routes don't yet enforce checkAccess.
// `apiResource` - the API base path the backend's checkAccess middleware guards and
//                 stores in roleschema.permissions[].resource. When present it is the
//                 key used BOTH when creating a role AND when matching GET /user
//                 permissions — see `pageResource()` below.
// `link`        - the frontend route; `label` is the display name.
export const DASHBOARD_PAGES = [
  { resource: "/", label: "Home", link: "/" },
  { resource: "/dashboard", label: "Dashboard", link: "/dashboard" },
  { resource: "/visit-stats", label: "Visit Stats", link: "/visit-stats" },
  { resource: "/task-stats", label: "Task Stats", link: "/task-stats" },
  { resource: "/update-location", label: "Dealer Location", link: "/update-location" },
  { resource: "/dealer-churn", label: "Dealer Churn", link: "/dealer-churn" },
  { resource: "/market-research", label: "Market Research", link: "/market-research" },
  { resource: "/actyv-ai", label: "Actyv AI", link: "/actyv-ai" },
  { resource: "/dealer-help-desk", label: "Ticketing", link: "/dealer-help-desk" },
  { resource: "/wear-nature", label: "Wear Nature", link: "/wear-nature" },
  { resource: "/survey", label: "Survey", link: "/survey" },
  { resource: "/survey-reconciliation", label: "Survey Reconciliation", link: "/survey-reconciliation" },
  { resource: "/compliance-inspect", label: "Compliance Inspector", link: "/compliance-inspect" },
  { resource: "/upload-documents", label: "Upload Docs", link: "/upload-documents" },
  { resource: "/holiday", label: "Holiday", link: "/holiday" },
  { resource: "/attendance-data", label: "Attendance", link: "/attendance-data" },
  { resource: "/vists-download", label: "Reports", link: "/vists-download" },
  { resource: "/dealer-directory", label: "Dealer Directory", link: "/dealer-directory" },
  // SE pages — keyed by their backend API base path (checkAccess enforcement starts here).
  { resource: "/se-depot", apiResource: "/api/v1/admin/se/all", label: "SE Depot & User", link: "/se-depot" },
  { resource: "/se-reports", apiResource: "/api/v1/report/corReportSE", label: "SE Reports", link: "/se-reports" },
  { resource: "/se-analytics", apiResource: "/api/v1/admin/se-analytics", label: "SE Analytics", link: "/se-analytics" },
  { resource: "/se-analytics/visits", apiResource: "/api/v1/admin/se-analytics/visits", label: "SE Analytics — Visits", link: "/se-analytics/visits" },
  { resource: "/se-analytics/daily-calls", apiResource: "/api/v1/admin/se-analytics/daily-calls", label: "SE Analytics — Daily Calls", link: "/se-analytics/daily-calls" },
  { resource: "/admin-otr", label: "Admin OTR", link: "/admin-otr" },
  { resource: "/onboard", label: "Onboard", link: "/onboard" },
  { resource: "/app-maintenance", label: "App Maintenance", link: "/app-maintenance" },
  { resource: "/force-update", label: "Force Update", link: "/force-update" },
];

// The permission key for a page: the backend API-path resource when defined,
// otherwise the legacy frontend-route resource. Used symmetrically on both sides
// (role creation AND GET /user matching) so the two can never drift apart.
export const pageResource = (pg) => pg.apiResource || pg.resource;

// The SE-specific pages — the only pages an SE admin may grant.
export const SE_PAGES = DASHBOARD_PAGES.filter((p) => p.resource.startsWith("/se-"));

// Zone/region scope only applies to the SE dashboard. Route key + API key.
export const SE_ANALYTICS_RESOURCE = "/se-analytics";
export const SE_ANALYTICS_API_RESOURCE = "/api/v1/admin/se-analytics";

// Roles created here get this role; all page access is via the permission matrix.
export const SUBADMIN_ROLE = "SUBADMIN";

// Build the set of granted permission keys (API-path or legacy route) from a
// permissions array — this is what GET /user returns in permissions[].resource.
export const grantedResources = (permissions = []) =>
  new Set(
    (permissions || [])
      .filter((p) => (p.actions || []).includes("view") || (p.actions || []).includes("edit"))
      .map((p) => p.resource)
  );

// The link of the first page a sub-admin is granted — used as their landing
// route after login. Returns null when nothing is granted.
export const firstGrantedLink = (permissions) => {
  const granted = grantedResources(permissions);
  const page = DASHBOARD_PAGES.find((pg) => granted.has(pageResource(pg)));
  return page ? page.link : null;
};

// Is the given pathname granted? Granted when the page itself OR any ancestor
// page is in the grant (so granting "/se-analytics" also covers its sub-pages).
export const isPathGranted = (pathname, permissions) => {
  const granted = grantedResources(permissions);
  return DASHBOARD_PAGES.some(
    (pg) =>
      (pathname === pg.link || pathname.startsWith(pg.link + "/")) &&
      granted.has(pageResource(pg))
  );
};
